import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  AppFormInput,
  AppFormSearchableSelect,
  AppFormTextarea,
} from '../../../components/common/appForms';
import Button from '../../../components/common/Button/Button';
import ConfirmModal from '../../../components/common/ConfirmModal/ConfirmModal';
import DecoratedHeader from '../../../components/common/DecoratedHeader/DecoratedHeader';
import Icon from '../../../components/common/Icon/Icon';
import { useMealTracking } from '../../../contexts/MealTrackingContext';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import type { MealEntry, MealFormValues } from '../../../types/mealTracking';
import pageStyles from '../../Checkout/CheckoutPage.module.css';
import fupStyles from '../../Fup/Fup.module.css';
import { MEAL_CATEGORY_OPTIONS } from '../mealTracking.constants';
import {
  createEmptyMealForm,
  mealToFormValues,
  validateMealForm,
} from '../mealTracking.utils';
import MealPhotoUploadField from './MealPhotoUploadField';
import styles from './MealFormModal.module.css';

interface MealFormModalProps {
  open: boolean;
  mealId?: string | null;
  defaultDate: string;
  onClose: () => void;
  onSaved: (meal: MealEntry, meta: { isCreate: boolean }) => void;
}

const MealFormModal: React.FC<MealFormModalProps> = ({
  open,
  mealId = null,
  defaultDate,
  onClose,
  onSaved,
}) => {
  const reduceMotion = useReducedMotion();
  const { getMealById, createMeal, updateMeal } = useMealTracking();
  const existingMeal = mealId ? getMealById(mealId) : undefined;
  const isEdit = Boolean(existingMeal);

  const [values, setValues] = useState<MealFormValues>(() => createEmptyMealForm(defaultDate));
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setValues(existingMeal ? mealToFormValues(existingMeal) : createEmptyMealForm(defaultDate));
    setErrors({});
    setIsDirty(false);
    setShowDiscardConfirm(false);
  }, [defaultDate, existingMeal, open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const requestClose = () => {
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }

    onClose();
  };

  useEscapeToClose(requestClose, open && !showDiscardConfirm);

  const updateField = <K extends keyof MealFormValues>(field: K, value: MealFormValues[K]) => {
    setIsDirty(true);
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors = validateMealForm(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    if (isEdit && mealId) {
      const updated = updateMeal(mealId, values);
      if (updated) {
        onSaved(updated, { isCreate: false });
      }
      return;
    }

    const created = createMeal(values);
    onSaved(created, { isCreate: true });
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            className={styles.overlay}
            role="presentation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.22 }}
          >
            <motion.div
              className={styles.panel}
              role="dialog"
              aria-modal="true"
              aria-labelledby="meal-form-modal-title"
              initial={reduceMotion ? { opacity: 0 } : { y: '100%' }}
              animate={reduceMotion ? { opacity: 1 } : { y: 0 }}
              exit={reduceMotion ? { opacity: 0 } : { y: '100%' }}
              transition={{ duration: reduceMotion ? 0.15 : 0.38, ease: [0.22, 1, 0.36, 1] }}
            >
              <header className={styles.header}>
                <button
                  type="button"
                  className={styles.closeButton}
                  onClick={requestClose}
                  aria-label="Fechar"
                >
                  <Icon name="close" size={22} />
                </button>
                <div className={styles.headerCopy}>
                  <DecoratedHeader
                    className={styles.title}
                    title={isEdit ? 'Editar refeição' : 'Adicionar refeição'}
                  />
                  <p id="meal-form-modal-title" className={pageStyles.headerSubtitle}>
                    Informe o tipo, horário e o que você comeu. Se puder, adicione uma foto da refeição.
                  </p>
                </div>
              </header>

              <form className={styles.form} onSubmit={handleSubmit} noValidate>
                <div className={pageStyles.formFields}>
                  <AppFormSearchableSelect
                    id="meal-category"
                    label="Tipo de refeição"
                    required
                    value={values.categoryId}
                    options={MEAL_CATEGORY_OPTIONS}
                    placeholder="Selecione uma opção"
                    noResultsText="Nenhum resultado encontrado"
                    allowEmpty={false}
                    onChange={(event) => updateField(
                      'categoryId',
                      event.target.value as MealFormValues['categoryId'],
                    )}
                  />

                  {values.categoryId === 'other' && (
                    <AppFormInput
                      id="meal-custom-title"
                      label="Nome da refeição"
                      value={values.customTitle}
                      onChange={(event) => updateField('customTitle', event.target.value)}
                      error={errors.customTitle}
                      required
                    />
                  )}

                  <div className={pageStyles.fieldRowHalf}>
                    <AppFormInput
                      id="meal-date"
                      label="Data"
                      type="date"
                      value={values.date}
                      onChange={(event) => updateField('date', event.target.value)}
                      error={errors.date}
                      required
                    />
                    <AppFormInput
                      id="meal-time"
                      label="Horário"
                      type="time"
                      value={values.time}
                      onChange={(event) => updateField('time', event.target.value)}
                      error={errors.time}
                      required
                    />
                  </div>

                  <AppFormTextarea
                    id="meal-description"
                    label="Descrição"
                    value={values.description}
                    onChange={(event) => updateField('description', event.target.value)}
                    error={errors.description}
                    placeholder="Ex.: Arroz, feijão, frango grelhado e salada"
                    required
                    minRows={3}
                  />

                  <div className={styles.uploadSection}>
                    <h2 className={pageStyles.cardTitle}>Fotos da refeição</h2>
                    <MealPhotoUploadField
                      photos={values.photos}
                      onChange={(photos) => updateField('photos', photos)}
                    />
                  </div>
                </div>

                <div className={fupStyles.footerActions}>
                  <div className={styles.actionsRow}>
                    <Button
                      type="button"
                      variant="outline"
                      size="app"
                      fullWidth
                      onClick={requestClose}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      size="app"
                      fullWidth
                      iconRight="arrow_forward"
                    >
                      Salvar refeição
                    </Button>
                  </div>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmModal
        open={showDiscardConfirm}
        title="Descartar alterações"
        message="Você tem alterações não salvas. Deseja sair sem salvar?"
        confirmLabel="Sair sem salvar"
        cancelLabel="Continuar editando"
        variant="danger"
        onCancel={() => setShowDiscardConfirm(false)}
        onConfirm={() => {
          setShowDiscardConfirm(false);
          onClose();
        }}
      />
    </>
  );
};

export default MealFormModal;
